import { useState, useEffect } from 'react';

/** Hard cap on the toggle-fetch so a stuck endpoint doesn't lock the checkout page. */
const FETCH_TIMEOUT_MS = 8000;

let inflightPromise = null;

/**
 * Fetch the current Defera Capital processor toggle from the server.
 *
 * Returns `'stripe' | 'nmi' | null` (null when the fetch failed or timed out).
 * Callers MUST treat `null` as "unknown" — never as a silent default to one
 * processor or the other. The previous version of this hook defaulted to
 * `'stripe'` on fetch errors, which is exactly how booking 5VOPA3 was misrouted
 * after the customer's tab lost the toggle value across a long-lived session.
 *
 * The dedup is per-render-cycle (in-flight) — we DON'T cache the resolved
 * value module-wide, because the admin can flip the toggle between mounts.
 */
function fetchDeferaCapitalProcessorOnce() {
  if (!inflightPromise) {
    inflightPromise = (async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      try {
        const r = await fetch('/api/defera-capital-processor', {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!r.ok) return null;
        const j = await r.json();
        if (j?.processor === 'nmi') return 'nmi';
        if (j?.processor === 'stripe') return 'stripe';
        return null;
      } catch {
        return null;
      } finally {
        clearTimeout(timeoutId);
        inflightPromise = null;
      }
    })();
  }
  return inflightPromise;
}

/**
 * Platform setting: which payment processor runs Defera Capital checkouts.
 *
 * Return values:
 *   - `null`     — still loading on initial mount, OR the most recent fetch
 *                  failed and we never had a value. Callers must show a loading
 *                  state and refuse to submit. Never assume a default here.
 *   - `'stripe'` — existing Stripe Subscriptions track.
 *   - `'nmi'`    — NMI / Vector Payments track.
 *
 * Refetch triggers:
 *   - Initial mount
 *   - Tab becomes visible again (visibilitychange)
 *   - Window regains focus (e.g. switching from another window)
 *
 * Why the refocus refetch matters: the original 5VOPA3 incident happened when
 * a customer kept Review.jsx open across a toggle flip and clicked Confirm
 * hours later with a stale local value. Refetching on focus closes the
 * obvious failure window without forcing a hard reload.
 *
 * A refresh that fails after we already had a value does NOT clear the value
 * back to `null` — that would cause the form to disappear on every tab focus
 * if the network is briefly unreliable. We keep the last-known value and let
 * the server-side guard in `defera-capital-create-subscription` catch any
 * actual mismatch.
 */
export function useDeferaCapitalProcessor() {
  const [processor, setProcessor] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const refresh = () => {
      fetchDeferaCapitalProcessorOnce().then((p) => {
        if (cancelled) return;
        if (p === 'stripe' || p === 'nmi') {
          setProcessor(p);
        }
      });
    };

    refresh();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', refresh);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  return processor;
}
